// Unpublished Work © 2022-2026 Deere & Company.

import equipmentStatusConstants from 'Ui/constants/equipment-status-constants';
import {formatLocalizedTime} from 'Utils/time-utils';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import moment from 'moment';
import {formatNumber, formatUoMValue, getFormatting} from 'Utils/unit-conversion-utils';
import {convert} from '@deere/unit-system';
import {configForUnit} from 'Common/utils/units/config-for-unit';
import {INCHES, MM} from 'Common/constants/data-unit-constants';
import {CONVERSION_UNIT, UNIT_OF_MEASURE} from 'Common/constants/preference-constants';

function formatStatus(equipment, translations) {
    const statusConstant = equipmentStatusConstants[equipment.status] || {};
    const statusLabel = translations[statusConstant.label] || statusConstant.label || equipment.status;

    return equipment.fleetId !== equipment.fleetInUseId && equipment.fleetInUseName ?
        `${statusLabel} @ ${equipment.fleetInUseName}` :
        statusLabel;
}

function getStatusLabelForEquipmentHistory(history, equipment, translations) {
    const statusConstant = equipmentStatusConstants[history.statusTo.toLowerCase()] || {};

    const statusLabel = translations[statusConstant.label] || statusConstant.label || history.statusTo;
    const canViewHistoryRecordLocation = history.assignedToId !== equipment.fleetId &&
        history.assignedToId !== equipment.shopId;

    return canViewHistoryRecordLocation && history.assignedToName ?
        `${statusLabel} @ ${history.assignedToName}` :
        statusLabel;
}

function formatTimeOrDefault(time) {
    if (time) {
        const date = moment(time).toDate();

        const formattedDate = formatLocalizedTime(date, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
        const formattedTime = formatLocalizedTime(date, {
            hour: 'numeric',
            minute: 'numeric'
        });

        return `${formattedDate} ${formattedTime}`;
    }

    return '';
}

function formatDateOrDefault(time) {
    return time && formatLocalizedTime(moment(time).toDate(), {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    }) || '';
}

function formatNumberOrDefault(number, precision) {
    return !isNullOrUndefined(number) ? formatNumber(number, {
        maximumFractionDigits: precision,
        minimumFractionDigits: precision
    }) : '';
}

function formatArrayToStringOrDefault(array, separator = ' ') {
    return array.filter((name) => !isNullOrUndefined(name)).join(separator) || '';
}

const CUT_HEIGHT_UNIT_BY_UOM = {
    [UNIT_OF_MEASURE.ENGLISH]: {
        conversionUnit: CONVERSION_UNIT.INCHES,
        formatUnit: INCHES,
        suffix: 'in'
    },
    [UNIT_OF_MEASURE.METRIC]: {
        conversionUnit: CONVERSION_UNIT.MILLIMETER,
        formatUnit: MM,
        suffix: 'mm'
    }
};

function formatEquipmentCutHeight(cutHeight, unitOfMeasure, translations) {
    if (!cutHeight || isNullOrUndefined(cutHeight.value)) {
        return {
            formattedCutHeight: '---',
            rawCutHeight: null
        };
    }

    const target = CUT_HEIGHT_UNIT_BY_UOM[unitOfMeasure] || CUT_HEIGHT_UNIT_BY_UOM[UNIT_OF_MEASURE.ENGLISH];
    const sourceUnit = cutHeight.unit || target.conversionUnit;
    const numericValue = Number.parseFloat(cutHeight.value);

    const convertedValue = sourceUnit === target.conversionUnit ?
        numericValue :
        convert(numericValue, sourceUnit, target.conversionUnit);

    return {
        formattedCutHeight: formatUoMValue(convertedValue, {
            formatting: getFormatting({
                ...configForUnit(target.formatUnit),
                includeSpace: true,
                suffix: target.suffix
            }),
            translations
        }),
        rawCutHeight: convertedValue
    };
}

export {
    formatStatus,
    formatDateOrDefault,
    formatTimeOrDefault,
    formatNumberOrDefault,
    getStatusLabelForEquipmentHistory,
    formatArrayToStringOrDefault,
    formatEquipmentCutHeight
};
